import { readonly, ref } from "vue";
import { type LimitRefusal, readLimitRefusal } from "@/shared/billing/limit-refusal";

/**
 * O TETO DE CANAIS DO PLANO, quando alguém bate nele — guardado onde a tela o
 * alcança.
 *
 * ── POR QUE ISTO EXISTE ───────────────────────────────────────────────────
 *
 * `POST /v3/channels`, `.../meta/connect` e `.../instagram/connect` recusam com
 * 400 `CHANNEL_LIMIT` e mandam `{ limit, used }` ao lado. São TRÊS portas — a
 * genérica do guia, o assistente da Meta e a volta do OAuth do Instagram — e
 * cada uma mostrava a recusa à sua maneira: um toast que passa em oito segundos,
 * um `failure` em vermelho, uma frase numa página de callback. Nenhuma das três
 * dizia quantos canais o plano dá, e nenhuma das três oferecia o que fazer.
 *
 * ── POR QUE ESTADO DE MÓDULO E NÃO UM `ref` NA PÁGINA ─────────────────────
 *
 * Porque as três portas são componentes de OUTRA feature, montados dentro da
 * página, e dois deles apanham o erro sem o deixar subir — a página não tem como
 * saber que houve uma recusa. Quem sabe é a mutação, que é daqui. O mesmo
 * desenho do `use-toast`: quem reporta o resultado é um callback de rede fundo
 * dentro de uma feature, sem instância de componente por onde injetar.
 *
 * A OFERTA em si não vive aqui: ela é da cobrança (`PlanLimitOffer`), e uma
 * feature não importa outra — a página é o único lugar que vê as duas.
 */
const refusal = ref<LimitRefusal | null>(null);

/**
 * Guarda a recusa se ela foi por teto, e diz se foi.
 *
 * O booleano é o que permite a quem chama CALAR o seu próprio aviso: um toast
 * "o seu plano não inclui mais um canal" por cima de um diálogo que diz a mesma
 * coisa com um botão é o mesmo recado duas vezes, e o que tem o botão é o que
 * some primeiro.
 */
export function noteChannelLimit(e: unknown): boolean {
	const read = readLimitRefusal(e);
	if (read?.code !== "CHANNEL_LIMIT") return false;
	refusal.value = read;
	return true;
}

/** A recusa por teto que está à espera de resposta, e como a dispensar. */
export function useChannelLimit() {
	return {
		channelLimit: readonly(refusal),
		dismissChannelLimit: (): void => {
			refusal.value = null;
		},
	};
}
